// Content data for /reports/[plan]/ SSG pages.
// Slugs are SEO-friendly (hyphens); plan_id maps to backend Razorpay plan IDs.
// Copy follows .claude/rules/22 — pain ("why does this keep happening?") →
// Vedic explanation → upay/CTA. No therapy or wellness framing.

import type { PremiumReportPlanId } from "@ishvaram/domain-data";
import { premiumReportPlanMap } from "@ishvaram/domain-data";

export interface ReportContent {
  slug: string;
  plan_id: PremiumReportPlanId;
  title_en: string;
  meta_description: string;
  hero_pain: string;
  hero_vedic: string;
  price_inr: number;
  strike_price_inr?: number;
  /**
   * Advertised page floor ("N+ page report"). Must stay <= the backend PDF QA
   * gate's min_pages for this plan_id (apps/backend .../reports/quality.py
   * REPORT_QUALITY_SPECS) — that gate blocks delivery of thinner PDFs, which is
   * what makes the claim guard-backed. If you lower a min_pages there, lower
   * this number first.
   */
  pdf_pages_floor: number;
  what_inside: string[];
  why_now: string;
  trust_lines: string[];
  faqs: { q: string; a: string }[];
}

const REPORT_CONTENT_SOURCE: Record<string, ReportContent> = {
  "brihat-kundali": {
    slug: "brihat-kundali",
    plan_id: "brihat_kundali",
    title_en: "Brihat Kundali — Full Vedic Birth Chart Reading | Ishvaram",
    meta_description:
      "Brihat Kundali reading: every house, planet, dasha and upay for your chart. Built from your birth details using Swiss Ephemeris, written like a senior Jyotishi sat with you.",
    hero_pain:
      "Why does life feel harder than it should — even when nothing on the outside is wrong?",
    hero_vedic:
      "Your Brihat Kundali reveals the planetary pattern most pandits never name aloud: which graha is creating the friction, which dasha you are walking through, and which upay actually moves the needle.",
    price_inr: 399,
    strike_price_inr: 999,
    pdf_pages_floor: 16,
    what_inside: [
      "All 12 bhavs read in plain language — what each house is doing for you, not textbook definitions",
      "Mahadasha + antardasha timeline with what to expect in the current and next period",
      "Lagna, Moon, and Navamsa cross-read — the three lenses every senior Jyotishi uses",
      "Yogas in your chart (Raj, Dhan, Gaja-Kesari, etc.) and what they unlock",
      "Doshas (Mangal, Kaal Sarp, Pitra, Sade Sati) checked with specific remedies — not generic mantras",
      "Readable 51-page private PDF, delivered through a secure Ishvaram download link in under 2 minutes",
    ],
    why_now:
      "Most people come back to their Brihat Kundali at three moments — career stall, marriage decision, family conflict. Better to know the pattern before the moment arrives.",
    trust_lines: [
      "Calculated with Swiss Ephemeris (the same engine drikpanchang.com uses)",
      "12,000+ Brihat readings delivered",
      "Written using Brihat Parashara Hora Shastra principles, not Western astrology",
    ],
    faqs: [
      {
        q: "How is this different from a free Kundli I can get anywhere?",
        a: "Free Kundli generators give you the chart wheel and stop there. The Brihat Kundali reading interprets every house, identifies the active mahadasha, names the yogas working in your favour, flags the doshas that need remedies, and gives you a personalised upay plan you can re-read when decisions come up.",
      },
      {
        q: "Will the upay (remedies) actually work?",
        a: "The upay in your Brihat Kundali are matched to the specific graha causing friction in your chart — not a generic 'wear ruby for Sun' list. Indian families have followed this lineage of remedies for centuries because they target the planetary pattern, not the symptom.",
      },
      {
        q: "What birth details do I need?",
        a: "Date of birth, time of birth (as accurate as possible — the panchang changes within minutes), and place of birth (city is enough; we resolve the exact coordinates).",
      },
      {
        q: "How quickly do I receive it?",
        a: "Under 2 minutes from payment. The PDF opens through a secure Ishvaram download link; if checkout recovery is needed, use the recovery email or write to support@ishvaram.com.",
      },
      {
        q: "What if I don't know my exact birth time?",
        a: "Use the best estimate. Most family records have it to within 30 minutes — that's accurate enough for the Brihat reading. For ultra-precise prashna analysis you'd need a separate rectification, which is not included here.",
      },
    ],
  },
  "marriage-report": {
    slug: "marriage-report",
    plan_id: "marriage_report",
    title_en: "Marriage Report — Kundali Milan, 7th House & Mangal Dosh | Ishvaram",
    meta_description:
      "Marriage Kundali reading: 7th house analysis, Mangal Dosh check, Kundali Milan with partner, and the upay that actually shifts marriage karma.",
    hero_pain: "Why does every relationship end the same way — even when the person was right?",
    hero_vedic:
      "Your 7th house, your Mangal placement and your Venus dasha decide the pattern. The Marriage Report names which one is repeating in your life and what to do about it.",
    price_inr: 199,
    strike_price_inr: 499,
    pdf_pages_floor: 14,
    what_inside: [
      "7th house full read — what kind of partner your chart is actually pulling in",
      "Mangal Dosh check (manglik or not, and how severe)",
      "Kundali Milan (Ashtakoota Gun Milan) with partner if you provide their birth details",
      "Nadi, Bhakoot, Gana dosh check and which can be remedied",
      "Marriage timing window per your current and next dasha",
      "Specific upay — not generic mantras — for the dosha in your chart",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Indian families take Kundali Milan seriously for a reason: 7th house karma repeats across relationships until the pattern is named and addressed.",
    trust_lines: [
      "Ashtakoota Gun Milan calculated per Brihat Parashara Hora Shastra",
      "Used by 4,200+ couples before marriage decisions",
      "Nadi normalisation fixed (Mar 2026) — no false Nadi Dosh reports",
    ],
    faqs: [
      {
        q: "What if my partner doesn't know their exact birth time?",
        a: "We can still run the 7th house + Mangal Dosh + dasha analysis on your chart alone. Kundali Milan with partner is the cleanest read when both birth times are accurate, but the marriage karma in your chart is readable independently.",
      },
      {
        q: "Is Mangal Dosh really a problem?",
        a: "Manglik placements only matter in specific combinations. The report tells you whether yours is severe (1st, 4th, 7th, 8th, 12th house from Lagna/Moon/Venus), nominally Mangal (cancelled by other yogas), or not Mangal at all. Most online Mangal calculators flag false positives.",
      },
      {
        q: "Will the upay break my Nadi Dosh?",
        a: "Nadi Dosh has specific cancellation rules in Vedic shastra (same nakshatra of different pada, exception nakshatras, etc.). The report applies those rules first and gives upay only for the residual dosh that actually remains.",
      },
      {
        q: "How is this different from a free Gun Milan score?",
        a: "Free Gun Milan gives you a number (X out of 36) and stops. The Marriage Report explains which kootas are weak, which are saved by other chart factors, what the dasha is going to do to the marriage in the next 5–7 years, and the specific upay for your dosha — not someone else's.",
      },
    ],
  },
  "career-report": {
    slug: "career-report",
    plan_id: "career_report",
    title_en: "Career Report — 10th House, Saturn & Career Timing | Ishvaram",
    meta_description:
      "Career Kundali reading: 10th house, Saturn placement, current mahadasha, and the timing of your next career break — by Vedic shastra, not a generic horoscope.",
    hero_pain:
      "Why does your hard work never seem to turn into results — even when others with less effort move ahead?",
    hero_vedic:
      "Your 10th house, your Shani placement and the dasha you're walking through decide career timing. The Career Report names exactly what's blocking and when the window opens.",
    price_inr: 199,
    strike_price_inr: 499,
    pdf_pages_floor: 8,
    what_inside: [
      "10th house (karma bhav) full read — what kind of career your chart actually supports",
      "Saturn (Shani) placement and Sade Sati status — the senior Jyotishi check most miss",
      "Current mahadasha + antardasha and what they mean for career timing",
      "Yogas that boost or block career — Raj yoga, Dhan yoga, Saraswati yoga",
      "Specific upay for the graha currently obstructing growth (not 'wear blue sapphire' guesswork)",
      "Profession suggestions matched to your chart, not a generic 'engineer or lawyer' list",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Career mahadashas typically last 6–19 years. Knowing whether the current one is opening doors or closing them changes the decisions you make this year.",
    trust_lines: [
      "Built from Swiss Ephemeris + Brihat Parashara Hora Shastra",
      "Sade Sati timing calibrated against drikpanchang.com",
      "10,000+ career reports delivered to Indian professionals",
    ],
    faqs: [
      {
        q: "Will the report tell me which job to take?",
        a: "It tells you which kinds of work your chart supports, which dasha period favours risk vs stability, and what upay to do for the planet currently blocking growth. The decision stays yours — but you make it with the planetary pattern in view.",
      },
      {
        q: "I'm in Sade Sati. Is everything going to fail?",
        a: "Sade Sati is not a curse. It's Shani giving you the lesson your chart already needed. The report tells you which phase (rising, peak, setting), what to avoid, and which upay specifically work for Shani in your chart's placement.",
      },
      {
        q: "Does this work for entrepreneurs / business owners?",
        a: "Yes — 10th house + 11th house + Mercury + Sun together read business chart very specifically. The report covers entrepreneurship explicitly when those factors are strong in your chart.",
      },
    ],
  },
  "annual-forecast": {
    slug: "annual-forecast",
    plan_id: "annual_forecast",
    title_en: "Annual Forecast (Varshphal) — Next 12 Months in Your Chart | Ishvaram",
    meta_description:
      "Varshphal report: month-by-month forecast for the next 12 months built from your solar return chart, current mahadasha, and transit positions.",
    hero_pain: "Why does this year feel different — and you can't put your finger on what shifted?",
    hero_vedic:
      "Every year your solar return chart (Varshphal) re-casts. Combined with your current mahadasha and the year's planetary transits, it tells you which months open and which need patience.",
    price_inr: 149,
    strike_price_inr: 499,
    pdf_pages_floor: 8,
    what_inside: [
      "Solar return chart (Varshphal) — your chart re-cast for this birth year",
      "Month-by-month forecast: career, finance, relationships, health, family",
      "Mahadasha + antardasha alignment with this year's transits",
      "Jupiter, Saturn and Rahu transit impact specific to your moon sign",
      "Auspicious dates per month for important decisions",
      "Upay calendar — which remedy to start in which month",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Most people start the year with intentions; few start it with the planetary pattern of their own chart for the year. The window the year opens is shorter than you think.",
    trust_lines: [
      "Varshphal calculated per Tajik Neelkanthi tradition",
      "Transits cross-checked against drikpanchang.com",
      "Monthly breakdowns, not a vague yearly summary",
    ],
    faqs: [
      {
        q: "When does the 12-month period start?",
        a: "From your next birthday (solar return) — that's when your Varshphal chart re-casts. If your birthday is 3 months away the report covers from then forward; if it just passed, the report starts immediately and runs 12 months.",
      },
      {
        q: "Is this the same as a horoscope?",
        a: "No. A horoscope is sun-sign + transits and applies to everyone with your rashi. The Varshphal is your specific chart's annual forecast — month-level granularity, dasha-aware, calibrated to your birth details.",
      },
      {
        q: "Do I need to combine this with the Brihat Kundali?",
        a: "They complement each other. Brihat Kundali is the full lifetime read; Varshphal is the 12-month operational view. Many readers buy the Brihat once and the Varshphal every year on their birthday.",
      },
    ],
  },
  "sade-sati": {
    slug: "sade-sati",
    plan_id: "sade_sati",
    title_en: "Sade Sati Report — Shani Phase, Dates & Upay | Ishvaram",
    meta_description:
      "Personal Sade Sati report: natal Shani, current phase, dasha context, pressure areas, and chart-specific Shani upay from your kundali.",
    hero_pain: "Why does this Shani phase feel heavy even when you are doing everything right?",
    hero_vedic:
      "Sade Sati is read from your Moon sign, but the real answer comes from natal Shani, current dasha, house pressure, and whether your chart can carry Saturn's discipline cleanly.",
    price_inr: 149,
    strike_price_inr: 499,
    pdf_pages_floor: 6,
    what_inside: [
      "Your current Sade Sati phase and the house pressure it creates from Moon sign",
      "Natal Shani placement, aspects, and whether Saturn is acting as a teacher or blocker",
      "Current mahadasha and antardasha context so the phase is not read in isolation",
      "Career, family, money, sleep, and mental-pressure areas separated clearly",
      "Chart-specific Shani upay: mantra, donation, Saturday discipline, and practical restraint",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Sade Sati can last 7.5 years, but every phase is different. Knowing whether you are in the rising, peak, or setting part changes what to avoid and what to build.",
    trust_lines: [
      "Moon-sign Sade Sati logic is combined with natal Saturn and dasha context",
      "Upay guidance is light, consistent, and chart-specific, not fear-based",
      "Built from the same kundali engine that powers Ishvaram chat and private reports",
    ],
    faqs: [
      {
        q: "Is Sade Sati always bad?",
        a: "No. Sade Sati is Shani testing discipline, maturity, and responsibility. The report separates pressure from actual danger by reading your Moon sign, natal Shani, house placement, and active dasha together.",
      },
      {
        q: "What details do I need?",
        a: "Date of birth, time of birth, and birth place. The Moon sign and house context decide the phase, so birth details should be as accurate as possible.",
      },
      {
        q: "Will it give remedies?",
        a: "Yes. It gives Shani upay matched to your chart: mantra, donation, Saturday discipline, restraint, and when a stronger shanti route is actually needed.",
      },
    ],
  },
  "pitra-dosh": {
    slug: "pitra-dosh",
    plan_id: "pitra_dosh",
    title_en: "Pitra Dosh Report — 9th House, Ancestors & Upay | Ishvaram",
    meta_description:
      "Pitra Dosh personal report: engine-verified diagnosis, 9th-house ancestral pattern, cancellation checks, dasha activation and safe upay.",
    hero_pain: "Why do family, career, or peace patterns keep repeating across generations?",
    hero_vedic:
      "Pitra Dosh should be confirmed through the Sun, 9th house, Rahu/Ketu, Saturn, and cancellation factors before any tarpan, shradh, or donation routine is recommended.",
    price_inr: 149,
    strike_price_inr: 499,
    pdf_pages_floor: 6,
    what_inside: [
      "Engine-verified Pitra Dosh check using Sun, 9th house, nodes, and 9th lord pressure",
      "Cancellation factors so a weak or neutral chart is not treated as a serious dosh",
      "Dasha activation window: when the ancestral pattern is likely to feel active",
      "Affected life areas: family peace, father line, career block, money pattern, or spiritual duty",
      "Safe upay sequence: tarpan, shradh, donation, mantra, and consistency notes",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Pitra Dosh upay should not start from fear. The safer order is diagnosis, severity, cancellation, activation, then the smallest effective upay.",
    trust_lines: [
      "Uses rule-backed Pitra Dosh detection before recommending any upay",
      "Cancellation checks are evaluated before severity is finalised",
      "Designed for Indian family decisions where ancestral karma is a serious concern",
    ],
    faqs: [
      {
        q: "How is this different from the free Pitra Dosh calculator?",
        a: "The calculator tells you whether a Pitra Dosh signature appears. The private report explains why it appears, whether cancellation reduces it, when it activates, and which upay sequence is safe for your chart.",
      },
      {
        q: "Does everyone need tarpan or shradh?",
        a: "No. The report checks severity first. Mild or cancelled patterns may need a lighter mantra/donation routine instead of a larger ritual.",
      },
      {
        q: "Can Pitra Dosh affect career?",
        a: "It can, especially when the 9th house, Sun, Saturn, or dasha links ancestral duty with karma and authority. The report explains which life area is actually touched in your chart.",
      },
    ],
  },
  "mahadasha-forecast": {
    slug: "mahadasha-forecast",
    plan_id: "mahadasha_forecast",
    title_en: "Mahadasha Forecast — Current Dasha & Next 12 Months | Ishvaram",
    meta_description:
      "Mahadasha forecast report: current mahadasha and antardasha, next 12 months of sub-periods, transition timing, and graha-specific upay.",
    hero_pain: "Why does timing change everything even when your effort stays the same?",
    hero_vedic:
      "Mahadasha decides the background climate of your life. The forecast reads the active graha period, the antardasha shift, and the next 12 months so decisions match timing.",
    price_inr: 199,
    strike_price_inr: 499,
    pdf_pages_floor: 6,
    what_inside: [
      "Current mahadasha and antardasha interpreted from your Moon nakshatra",
      "Next 12 months of sub-periods with practical timing notes",
      "Career, money, marriage, family, health, and spiritual focus by active graha",
      "Transition points where the emotional or practical climate changes",
      "Graha-specific upay for the period instead of generic yearly advice",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "The same action taken in the wrong dasha window feels blocked. The same action in the right window becomes easier. This report helps you stop fighting timing blindly.",
    trust_lines: [
      "Vimshottari dasha is computed from Moon nakshatra, not guessed from sun sign",
      "Forecast connects dasha with transits and practical decision windows",
      "Built for repeat reading across the year, not one-time curiosity",
    ],
    faqs: [
      {
        q: "Is Mahadasha the same as an annual forecast?",
        a: "No. Mahadasha is the long planetary period behind your life timing. Annual forecast is the year view. This report focuses on the active dasha and the next 12 months of sub-period movement.",
      },
      {
        q: "Do I need exact birth time?",
        a: "Birth time improves the house context, but Moon nakshatra drives Vimshottari dasha. Use the most accurate time available so the supporting chart remains clean.",
      },
      {
        q: "Will it tell me good and bad months?",
        a: "It gives action, caution, and upay windows by sub-period. It avoids fixed guarantees and explains the graha pattern behind each period.",
      },
    ],
  },
  "muhurat-report": {
    slug: "muhurat-report",
    plan_id: "muhurat_report",
    title_en: "Muhurat Report — Personal Auspicious Timing & Sankalp | Ishvaram",
    meta_description:
      "Personal muhurat report for important decisions: city panchang, purpose-specific timing, avoid windows, sankalp, and ritual checklist from your birth chart.",
    hero_pain: "Why does a shubh time from the internet still feel wrong for your specific work?",
    hero_vedic:
      "A real muhurat depends on your purpose, city, Moon sign, dasha, tithi, nakshatra, yoga, karana, Rahu Kaal and personal chart pressure. This report brings those layers together.",
    price_inr: 149,
    strike_price_inr: 999,
    pdf_pages_floor: 6,
    what_inside: [
      "Purpose-specific timing guidance for launch, purchase, griha pravesh, travel, naming, ritual, or family decision",
      "Panchang layer: tithi, nakshatra, yoga, karana, weekday and choghadiya",
      "Avoid windows: Rahu Kaal, Yamagandam, Gulika Kaal and personal dasha pressure",
      "Best available windows ranked with practical recommendation notes",
      "Sankalp wording and ritual checklist so the timing is actionable",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Muhurat is useful only before the decision is made. Once the event is fixed, the best you can do is damage control; this report helps you choose the cleaner window first.",
    trust_lines: [
      "Built from city panchang and chart-aware timing rules",
      "Avoid-window scoring includes Rahu Kaal and dasha context",
      "Designed to beat public choghadiya tables with personal context",
    ],
    faqs: [
      {
        q: "How is this different from a choghadiya table?",
        a: "A public choghadiya table gives a general day/night period for a city. The Muhurat Report adds your purpose, birth-chart context, avoid windows, and sankalp checklist so you know which window to use and why.",
      },
      {
        q: "Can I use it for marriage?",
        a: "Use this for early timing guidance. A full wedding muhurat still needs deeper family constraints, both charts, and final pandit confirmation because marriage timing has stricter rules.",
      },
      {
        q: "What details do I need?",
        a: "Your birth details, city, purpose, and date range. A wider date range gives the engine more room to find a clean window.",
      },
    ],
  },
  "dosha-remedy-plan": {
    slug: "dosha-remedy-plan",
    plan_id: "dosha_remedy_plan",
    title_en: "Dosha Remedy Plan — Severity, Cancellation & Safe Upay | Ishvaram",
    meta_description:
      "Chart-specific dosha diagnosis and remedy plan: Mangal, Kaal Sarp, Pitra, Shani and other dosha checks with severity, cancellation rules and safe upay.",
    hero_pain: "Why do people keep telling you to do upay before confirming the dosha?",
    hero_vedic:
      "A dosha should be diagnosed by rule, severity and cancellation first. The Dosha Remedy Plan shows which dosha actually fires in your chart and which upay is safe for that pattern.",
    price_inr: 199,
    strike_price_inr: 999,
    pdf_pages_floor: 6,
    what_inside: [
      "Detected doshas with the rule that fired, not a generic fear-based list",
      "Severity and cancellation checks before any remedy is recommended",
      "Life areas affected: marriage, career, family, health, money or spiritual discipline",
      "Safe upay routine: mantra, donation, vrata, puja timing and behavioural correction",
      "Matched remedy products only where the chart supports them",
      "Readable private PDF delivered through a secure Ishvaram report link",
    ],
    why_now:
      "Wrong remedies waste money and can create fear. The safer path is to confirm the dosha, check cancellation, then follow the smallest effective upay consistently.",
    trust_lines: [
      "Engine checks dosha rules before recommending remedies",
      "Cancellation factors are evaluated before severity is finalised",
      "Remedy guidance is chart-specific and avoids gemstone overclaiming",
    ],
    faqs: [
      {
        q: "Does every chart have a dosha?",
        a: "No. Many online tools over-flag doshas because they skip cancellation rules. This report separates real, cancelled, mild and high-severity cases.",
      },
      {
        q: "Will it recommend gemstones?",
        a: "Only when the chart supports that route. Most dosha cases are safer with mantra, donation, vrata, puja timing and behaviour-based correction before any gemstone.",
      },
      {
        q: "Which doshas are checked?",
        a: "Mangal Dosh, Kaal Sarp, Pitra Dosh, Shani/Sade Sati pressure and other chart-specific afflictions available in the Ishvaram engine.",
      },
    ],
  },
};

// Permanent single-source-of-truth for the CHARGED price: price_inr is ALWAYS
// derived from the canonical premiumReportPlanMap (generated from the report
// catalog SoT, the same source the Razorpay money path reads). A report page can
// therefore never display a price that differs from what is charged — closing the
// 2026-06-18 marriage ₹149/₹199 drift class permanently. The price_inr literals in
// REPORT_CONTENT_SOURCE are display documentation only; this overlay overrides them.
export const REPORT_CONTENT: Record<string, ReportContent> = Object.fromEntries(
  Object.entries(REPORT_CONTENT_SOURCE).map(([slug, content]) => [
    slug,
    { ...content, price_inr: premiumReportPlanMap[content.plan_id].price_inr },
  ])
);

export const REPORT_SLUGS = Object.keys(REPORT_CONTENT);

/** Guard-backed "N+ page" floor for a plan, for offer surfaces (chat nudge, paywall). */
export function pagesFloorForPlan(planId: string): number | null {
  const entry = Object.values(REPORT_CONTENT).find((report) => report.plan_id === planId);
  return entry?.pdf_pages_floor ?? null;
}
